import { useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/* ─────────────────────────────────────────────────────────────────────────
   L17.07 — Bulk Import & Data Quality (`/admin/master-config/bulk`).
   Covers §27 bulk upload and §28 data-quality checks over `v1/m17/bulk/*`.
   The bulk endpoints validate their bodies manually (not via the strict DTO
   whitelist), so `{ rows }` / record payloads are accepted directly.
   ───────────────────────────────────────────────────────────────────────── */

interface UploadResult {
  readonly totalRows?: number;
  readonly successCount?: number;
  readonly failureCount?: number;
  readonly errors?: readonly { readonly rowNumber?: number; readonly message?: string }[];
}

interface QualityResult {
  readonly entityType?: string;
  readonly totalRecords?: number;
  readonly issueCount?: number;
  readonly issues?: readonly { readonly field?: string; readonly message?: string }[];
}

export function M17BulkImportPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [entityType, setEntityType] = useState('');
  const [rowsText, setRowsText] = useState('');
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [qualityResult, setQualityResult] = useState<QualityResult | null>(null);
  const [approvalIdsText, setApprovalIdsText] = useState('');
  const [approvalAction, setApprovalAction] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [approvalNotice, setApprovalNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onUpload(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setUploadResult(null);
    if (!entityType) { setError(t('m17.bulkImport.errEntityRequired')); return; }
    let rows: unknown;
    try {
      rows = JSON.parse(rowsText || '[]');
    } catch {
      setError(t('m17.bulkImport.errRowsJson'));
      return;
    }
    if (!Array.isArray(rows)) { setError(t('m17.bulkImport.errRowsArray')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<UploadResult>(`/v1/m17/bulk/upload/${encodeURIComponent(entityType)}`, {
        method: 'POST', token, body: { rows },
      });
      setUploadResult(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onRunQuality(): Promise<void> {
    setError(null);
    setQualityResult(null);
    if (!entityType) { setError(t('m17.bulkImport.errEntityRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<QualityResult>(`/v1/m17/bulk/quality/${encodeURIComponent(entityType)}`, { token });
      setQualityResult(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onBulkApprove(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setApprovalNotice(null);
    const approvalIds = approvalIdsText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (approvalIds.length === 0) { setError(t('m17.bulkImport.errApprovalId')); return; }
    setBusy(true);
    try {
      await apiRequest('/v1/m17/bulk/approve', {
        method: 'POST', token,
        body: { approvalIds, action: approvalAction, remarks: approvalRemarks },
      });
      setApprovalNotice(
        approvalAction === 'APPROVE'
          ? t('m17.bulkImport.noticeApproved', { count: approvalIds.length })
          : t('m17.bulkImport.noticeRejected', { count: approvalIds.length }),
      );
      setApprovalIdsText('');
      setApprovalRemarks('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="bulk-import">
      <h1>{t('m17.bulkImport.title')}</h1>

      <label htmlFor="bi-entity">{t('m17.bulkImport.entityType')}</label>
      <input id="bi-entity" aria-label="entity-type" value={entityType} onChange={(e) => setEntityType(e.target.value)} placeholder={t('m17.bulkImport.entityPlaceholder')} />

      <form onSubmit={onUpload} aria-label="bulk-upload-form" noValidate>
        <h2>{t('m17.bulkImport.uploadRows')}</h2>
        <label htmlFor="bi-rows">{t('m17.bulkImport.rowsLabel')}</label>
        <textarea id="bi-rows" aria-label="rows-json" value={rowsText} onChange={(e) => setRowsText(e.target.value)} rows={6} />
        <button type="submit" disabled={busy}>{t('m17.bulkImport.uploadBtn')}</button>
      </form>

      <section>
        <h2>{t('m17.bulkImport.dataQuality')}</h2>
        <button type="button" aria-label="run-quality" disabled={busy} onClick={onRunQuality}>{t('m17.bulkImport.runQualityBtn')}</button>
      </section>

      <form onSubmit={onBulkApprove} aria-label="bulk-approve-form" noValidate>
        <h2>{t('m17.bulkImport.bulkApproval')}</h2>
        <label htmlFor="ba-ids">{t('m17.bulkImport.approvalIdsLabel')}</label>
        <textarea id="ba-ids" aria-label="approval-ids" value={approvalIdsText} onChange={(e) => setApprovalIdsText(e.target.value)} rows={2} />
        <label htmlFor="ba-action">{t('m17.bulkImport.actionLabel')}</label>
        <select id="ba-action" aria-label="approval-action" value={approvalAction} onChange={(e) => setApprovalAction(e.target.value as 'APPROVE' | 'REJECT')}>
          <option value="APPROVE">{t('m17.bulkImport.optApprove')}</option>
          <option value="REJECT">{t('m17.bulkImport.optReject')}</option>
        </select>
        <label htmlFor="ba-remarks">{t('m17.bulkImport.remarks')}</label>
        <input id="ba-remarks" value={approvalRemarks} onChange={(e) => setApprovalRemarks(e.target.value)} />
        <button type="submit" disabled={busy}>{t('m17.bulkImport.submitBtn')}</button>
      </form>

      {approvalNotice ? <p role="status">{approvalNotice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      {uploadResult ? (
        <section aria-label="upload-result">
          <h3>{t('m17.bulkImport.uploadResult')}</h3>
          <p>{t('m17.bulkImport.uploadSummary', { total: uploadResult.totalRows ?? 0, success: uploadResult.successCount ?? 0, failed: uploadResult.failureCount ?? 0 })}</p>
          {uploadResult.errors && uploadResult.errors.length > 0 ? (
            <table aria-label="upload-errors-table">
              <thead><tr><th>{t('m17.bulkImport.colRow')}</th><th>{t('m17.bulkImport.colMessage')}</th></tr></thead>
              <tbody>
                {uploadResult.errors.map((er, i) => (
                  <tr key={i}><td>{er.rowNumber ?? '—'}</td><td>{er.message ?? '—'}</td></tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      ) : null}

      {qualityResult ? (
        <section aria-label="quality-result">
          <h3>{t('m17.bulkImport.qualityResult')}</h3>
          <p>{t('m17.bulkImport.qualitySummary', { records: qualityResult.totalRecords ?? 0, issues: qualityResult.issueCount ?? qualityResult.issues?.length ?? 0 })}</p>
          {qualityResult.issues && qualityResult.issues.length > 0 ? (
            <table aria-label="quality-issues-table">
              <thead><tr><th>{t('m17.bulkImport.colField')}</th><th>{t('m17.bulkImport.colMessage')}</th></tr></thead>
              <tbody>
                {qualityResult.issues.map((is, i) => (
                  <tr key={i}><td>{is.field ?? '—'}</td><td>{is.message ?? '—'}</td></tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
